"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.TelemetryClient = void 0;
const api_1 = require("@opentelemetry/api");
const api_logs_1 = require("@opentelemetry/api-logs");
const semantic_conventions_1 = require("@opentelemetry/semantic-conventions");
const context_1 = require("./context");
const util_1 = require("../shared/util");
const Config = require("./shim-config");
const attributeSpanProcessor_1 = require("../shared/util/attributeSpanProcessor");
const attributeLogRecordProcessor_1 = require("../shared/util/attributeLogRecordProcessor");
const logsApi_1 = require("./logsApi");
const main_1 = require("../main");
const telemetryClientProvider_1 = require("./telemetryClientProvider");
const types_1 = require("./types");
const statsbeatFeaturesManager_1 = require("../shared/util/statsbeatFeaturesManager");
/**
 * Application Insights telemetry client provides interface to track telemetry items, register telemetry initializers and
 * and manually trigger immediate sending (flushing)
 */
class TelemetryClient {
    /**
     * Constructs a new instance of TelemetryClient
     * @param setupString the Connection String or Instrumentation Key to use (read from environment variable if not specified)
     */
    constructor(input, options) {
        var _a;
        this._configWarnings = [];
        TelemetryClient._instanceCount++;
        // Set statsbeat feature if this is the second or subsequent TelemetryClient instance
        if (TelemetryClient._instanceCount >= 2) {
            statsbeatFeaturesManager_1.StatsbeatFeaturesManager.getInstance().enableFeature(types_1.StatsbeatFeature.MULTI_IKEY);
        }
        const config = new Config(input, this._configWarnings);
        this.config = config;
        this.commonProperties = {};
        this.context = new context_1.Context();
        this._isInitialized = false;
        this._useGlobalProviders = (_a = options === null || options === void 0 ? void 0 : options.useGlobalProviders) !== null && _a !== void 0 ? _a : true;
    }
    initialize() {
        if (this._isInitialized) {
            return;
        }
        this._isInitialized = true;
        // Parse shim config to Azure Monitor options
        this._options = this.config.parseConfig();
        try {
            this._attributeSpanProcessor = new attributeSpanProcessor_1.AttributeSpanProcessor(Object.assign(Object.assign({}, this.context.tags), this.commonProperties));
            this._attributeLogProcessor = new attributeLogRecordProcessor_1.AttributeLogProcessor(Object.assign(Object.assign({}, this.context.tags), this.commonProperties));
            this._options.spanProcessors = [...(this._options.spanProcessors || []), this._attributeSpanProcessor];
            this._options.logRecordProcessors = [...(this._options.logRecordProcessors || []), this._attributeLogProcessor];
            if (this._useGlobalProviders) {
                (0, main_1.useAzureMonitor)(this._options);
            }
            else {
                this._telemetryClientProvider = new telemetryClientProvider_1.TelemetryClientProvider(this._options);
            }
            const logger = this._useGlobalProviders
                ? api_logs_1.logs.getLogger("ApplicationInsightsLogger")
                : this._telemetryClientProvider.getLogger("ApplicationInsightsLogger");
            this._logApi = new logsApi_1.LogApi(logger);
            // Warn if any config warnings were generated during parsing
            for (let i = 0; i < this._configWarnings.length; i++) {
                api_1.diag.warn(this._configWarnings[i]);
            }
        }
        catch (error) {
            api_1.diag.error(`Failed to initialize TelemetryClient ${error}`);
        }
    }
    /**
     * Log information about availability of an application
     * @param telemetry      Object encapsulating tracking options
     */
    trackAvailability(telemetry) {
        if (!this._isInitialized) {
            this.initialize();
        }
        this._logApi.trackAvailability(telemetry);
    }
    /**
     * Log a page view
     * @param telemetry      Object encapsulating tracking options
     */
    trackPageView(telemetry) {
        if (!this._isInitialized) {
            this.initialize();
        }
        this._logApi.trackPageView(telemetry);
    }
    /**
     * Log a trace message
     * @param telemetry      Object encapsulating tracking options
     */
    trackTrace(telemetry) {
        if (!this._isInitialized) {
            this.initialize();
        }
        this._logApi.trackTrace(telemetry);
    }
    /**
     * Log an exception
     * @param telemetry      Object encapsulating tracking options
     */
    trackException(telemetry) {
        if (!this._isInitialized) {
            this.initialize();
        }
        this._logApi.trackException(telemetry);
    }
    /**
     * Log a user action or other occurrence.
     * @param telemetry      Object encapsulating tracking options
     */
    trackEvent(telemetry) {
        if (!this._isInitialized) {
            this.initialize();
        }
        this._logApi.trackEvent(telemetry);
    }
    /**
     * Log a numeric value that is not associated with a specific event. Typically used to send regular reports of performance indicators.
     * To send a single measurement, use just the first two parameters. If you take measurements very frequently, you can reduce the
     * telemetry bandwidth by aggregating multiple measurements and sending the resulting average at intervals.
     * @param telemetry      Object encapsulating tracking options
     */
    trackMetric(telemetry) {
        if (!this._isInitialized) {
            this.initialize();
        }
        // Create custom metric
        try {
            const meter = this._getMeterInstance();
            const histogram = meter.createHistogram(telemetry.name);
            histogram.record(telemetry.value, Object.assign(Object.assign(Object.assign({}, telemetry.properties), this.commonProperties), this.context.tags));
        }
        catch (error) {
            api_1.diag.error(`Failed to record metric: ${error}`);
        }
    }
    _getTracerInstance() {
        if (this._telemetryClientProvider) {
            if (!this._manualTracer) {
                this._manualTracer = this._telemetryClientProvider.getTracer("ApplicationInsightsTracer");
            }
            return this._manualTracer;
        }
        return api_1.trace.getTracer("ApplicationInsightsTracer");
    }
    _getMeterInstance() {
        if (this._telemetryClientProvider) {
            if (!this._manualMeter) {
                this._manualMeter = this._telemetryClientProvider.getMeter("ApplicationInsightsMetrics");
            }
            return this._manualMeter;
        }
        return api_1.metrics.getMeterProvider().getMeter("ApplicationInsightsMetrics");
    }
    /**
     * Log a request. Note that the default client will attempt to collect HTTP requests automatically so only use this for requests
     * that aren't automatically captured or if you've disabled automatic request collection.
     *
     * @param telemetry      Object encapsulating tracking options
     */
    trackRequest(telemetry) {
        if (!this._isInitialized) {
            this.initialize();
        }
        // For trackRequest, when time is not specified, treat current time as END time
        // since this method is called after the request operation has completed
        const endTime = telemetry.time ? telemetry.time.getTime() + telemetry.duration : Date.now();
        const startTime = telemetry.time || new Date(endTime - telemetry.duration);
        const ctx = api_1.context.active();
        const attributes = Object.assign({}, telemetry.properties);
        // Only set HTTP attributes if we have the relevant data
        if (!telemetry.name) {
            attributes[semantic_conventions_1.SEMATTRS_HTTP_METHOD] = "HTTP";
        }
        if (telemetry.url) {
            attributes[semantic_conventions_1.SEMATTRS_HTTP_URL] = telemetry.url;
        }
        if (telemetry.resultCode) {
            attributes[semantic_conventions_1.SEMATTRS_HTTP_STATUS_CODE] = telemetry.resultCode;
        }
        const options = {
            kind: api_1.SpanKind.SERVER,
            attributes: attributes,
            startTime: startTime,
        };
        const span = this._getTracerInstance()
            .startSpan(telemetry.name, options, ctx);
        if (telemetry.id) {
            try {
                if (span._spanContext) {
                    span._spanContext.traceId = telemetry.id;
                }
            }
            catch (error) {
                api_1.diag.warn('Unable to set custom traceId on span:', error);
            }
        }
        span.setStatus({
            code: telemetry.success ? api_1.SpanStatusCode.OK : api_1.SpanStatusCode.ERROR,
        });
        span.end(endTime);
    }
    /**
     * Log a dependency. Note that the default client will attempt to collect dependencies automatically so only use this for dependencies
     * that aren't automatically captured or if you've disabled automatic dependency collection.
     *
     * @param telemetry      Object encapsulating tracking option
     * */
    trackDependency(telemetry) {
        if (!this._isInitialized) {
            this.initialize();
        }
        // For trackDependency, when time is not specified, treat current time as END time
        // since this method is called after the dependency operation has completed
        const endTime = telemetry.time ? telemetry.time.getTime() + telemetry.duration : Date.now();
        const startTime = telemetry.time || new Date(endTime - telemetry.duration);
        if (telemetry && !telemetry.target && telemetry.data) {
            // url.parse().host returns null for non-urls,
            // making this essentially a no-op in those cases
            // If this logic is moved, update jsdoc in DependencyTelemetry.target
            // url.parse() is deprecated, update to use WHATWG URL API instead
            try {
                telemetry.target = new URL(telemetry.data).host;
            }
            catch (error) {
                // set target as null to be compliant with previous behavior
                telemetry.target = null;
                api_1.diag.warn(this.constructor.name, "Failed to create URL.", error);
            }
        }
        const ctx = api_1.context.active();
        const attributes = Object.assign({}, telemetry.properties);
        if (telemetry.dependencyTypeName) {
            if (telemetry.dependencyTypeName.toLowerCase().indexOf("http") > -1) {
                // Only set HTTP URL and status code for HTTP dependencies
                if (telemetry.data) {
                    attributes[semantic_conventions_1.SEMATTRS_HTTP_URL] = telemetry.data;
                }
                if (telemetry.resultCode) {
                    attributes[semantic_conventions_1.SEMATTRS_HTTP_STATUS_CODE] = telemetry.resultCode;
                }
            }
            else if (util_1.Util.getInstance().isDbDependency(telemetry.dependencyTypeName)) {
                attributes[semantic_conventions_1.SEMATTRS_DB_SYSTEM] = telemetry.dependencyTypeName;
                attributes[semantic_conventions_1.SEMATTRS_DB_STATEMENT] = telemetry.data;
            }
            else if (telemetry.dependencyTypeName.toLowerCase().indexOf("rpc") > -1) {
                attributes[semantic_conventions_1.SEMATTRS_RPC_SYSTEM] = telemetry.dependencyTypeName;
                attributes[semantic_conventions_1.SEMATTRS_RPC_METHOD] = telemetry.data;
                attributes[semantic_conventions_1.SEMATTRS_RPC_GRPC_STATUS_CODE] = telemetry.resultCode;
            }
        }
        if (telemetry.target) {
            attributes[semantic_conventions_1.SEMATTRS_PEER_SERVICE] = telemetry.target;
        }
        const options = {
            kind: api_1.SpanKind.CLIENT,
            attributes: attributes,
            startTime: startTime,
        };
        const span = this._getTracerInstance()
            .startSpan(telemetry.name, options, ctx);
        if (telemetry.id) {
            try {
                if (span._spanContext) {
                    span._spanContext.traceId = telemetry.id;
                }
            }
            catch (error) {
                api_1.diag.warn('Unable to set custom traceId on span:', error);
            }
        }
        span.setStatus({
            code: telemetry.success ? api_1.SpanStatusCode.OK : api_1.SpanStatusCode.ERROR,
        });
        span.end(endTime);
    }
    /**
     * Generic track method for all telemetry types
     * @param data the telemetry to send
     * @param telemetryType specify the type of telemetry you are tracking from the list of Contracts.DataTypes
     */
    track(telemetry, telemetryType) {
        throw new Error(`Not implemented. Please use the specific track method for the type of telemetry you are tracking. ${types_1.UNSUPPORTED_MSG}`);
    }
    /**
     * Automatically populate telemetry properties like RoleName when running in Azure
     *
     * @param value if true properties will be populated
     */
    setAutoPopulateAzureProperties() {
        // NO-OP
    }
    /**
     * Get Authorization handler
     */
    getAuthorizationHandler(config) {
        api_1.diag.warn(`getAuthorizationHandler is not supported in ApplicationInsights any longer. ${types_1.UNSUPPORTED_MSG}`);
    }
    /*
     * Get Statsbeat instance
     */
    getStatsbeat() {
        return null;
    }
    setUseDiskRetryCaching(value, resendInterval, maxBytesOnDisk) {
        throw new Error("Not implemented");
    }
    /**
     * Adds telemetry processor to the collection. Telemetry processors will be called one by one
     * before telemetry item is pushed for sending and in the order they were added.
     *
     * @param telemetryProcessor function, takes Envelope, and optional context object and returns boolean
     */
    addTelemetryProcessor(telemetryProcessor) {
        api_1.diag.warn(`addTelemetryProcessor is not supported in ApplicationInsights any longer. ${types_1.UNSUPPORTED_MSG}`);
    }
    /*
     * Removes all telemetry processors
     */
    clearTelemetryProcessors() {
        throw new Error("Not implemented");
    }
    trackNodeHttpRequestSync(telemetry) {
        api_1.diag.warn("trackNodeHttpRequestSync is not implemented and is a no-op. Please use trackRequest instead.");
    }
    trackNodeHttpRequest(telemetry) {
        api_1.diag.warn("trackNodeHttpRequest is not implemented and is a no-op. Please use trackRequest instead.");
    }
    trackNodeHttpDependency(telemetry) {
        api_1.diag.warn("trackNodeHttpDependency is not implemented and is a no-op. Please use trackDependency instead.");
    }
    /**
    * Immediately send all queued telemetry.
    */
    async flush() {
        if (this._telemetryClientProvider) {
            return this._telemetryClientProvider.flush();
        }
        return (0, main_1.flushAzureMonitor)();
    }
    /**
     * Shutdown client
     */
    async shutdown() {
        if (this._telemetryClientProvider) {
            return this._telemetryClientProvider.shutdown();
        }
        return (0, main_1.shutdownAzureMonitor)();
    }
    pushWarningToLog(warning) {
        this._configWarnings.push(warning);
    }
}
exports.TelemetryClient = TelemetryClient;
TelemetryClient._instanceCount = 0;
//# sourceMappingURL=telemetryClient.js.map